'use client'

import Link from 'next/link'
import { LuCheck, LuEllipsis, LuPencil, LuTrash2 } from 'react-icons/lu'

import { Button } from '@/core/components/form/button/button'
import DropdownMenu from '@/core/components/dropdown-menu'
import { cn } from '@/core/utils/common'

import SafeImage from '@/features/veluvit/components/ui/media-image'
import { personaPhotoUrl } from '@/features/veluvit/lib/persona-photo'
import { go } from '@/features/veluvit/lib/navigation'
import { to } from '@/features/veluvit/lib/route'

import type { SavedPersona } from '@/features/veluvit/types/api.types'

export function PersonaAvatar({
  avatar,
  name = '나',
  className,
  preview,
}: {
  avatar?: string
  name?: string
  className?: string
  preview?: string
}) {
  const photo = preview || personaPhotoUrl(avatar)
  return (
    <span
      aria-hidden="true"
      className={cn(
        'relative grid size-12 flex-none place-items-center overflow-hidden rounded-full bg-foreground/[0.07] text-c17 font-semibold text-t2',
        className
      )}
    >
      {photo ? (
        <SafeImage
          src={photo}
          alt=""
          fill
          sizes="96px"
          unoptimized={photo.startsWith('blob:')}
          appearance="plain"
          className="object-cover object-center"
        />
      ) : (
        name.trim().slice(0, 1) || '나'
      )}
    </span>
  )
}

export default function PersonaCard({
  persona: p,
  onSelect,
  onDefault,
  onDelete,
  compact,
  busy = false,
}: {
  persona: SavedPersona
  onSelect?: () => void
  onDefault?: () => void
  onDelete?: () => void
  compact?: boolean
  busy?: boolean
}) {
  const meta = [p.age, p.gender !== '설정 안 함' ? p.gender : '', p.role]
    .filter(Boolean)
    .join(' · ')
  const content = (
    <>
      <PersonaAvatar avatar={p.avatar} name={p.name} />
      <span className="min-w-0 flex-1 text-left">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <b className="text-c16 leading-6 font-semibold text-t1 over10:text-c17">
            {p.name}
          </b>
          {p.isDefault && (
            <span className="inline-flex items-center gap-1 text-c12 font-medium text-t3">
              <LuCheck aria-hidden="true" className="size-3.5" />
              기본
            </span>
          )}
        </span>
        {meta && (
          <span className="mt-0.5 block truncate text-c13 text-t3">{meta}</span>
        )}
        {(p.description || p.personality) && (
          <span className="mt-1.5 line-clamp-2 whitespace-pre-line text-c14 leading-relaxed text-t2">
            {p.description || p.personality}
          </span>
        )}
      </span>
    </>
  )
  if (onSelect)
    return (
      <Button
        variant="ghost"
        shape="field"
        className="w-full justify-start! gap-4! px-2! py-4!"
        disabled={busy}
        onClick={onSelect}
      >
        {content}
      </Button>
    )
  const actions = [
    {
      id: 'edit',
      label: '편집하기',
      icon: <LuPencil />,
      disabled: busy,
      onClick: () => go(`/persona-edit/${p.id}`),
    },
    ...(!p.isDefault && onDefault
      ? [
          {
            id: 'default',
            label: '기본으로 설정',
            icon: <LuCheck />,
            disabled: busy,
            onClick: onDefault,
          },
        ]
      : []),
    ...(onDelete
      ? [
          {
            id: 'delete',
            label: '삭제하기',
            icon: <LuTrash2 />,
            danger: true,
            disabled: busy,
            onClick: onDelete,
          },
        ]
      : []),
  ]
  return (
    <article
      className={cn(
        'flex min-w-0 items-start gap-3 border-b border-border py-5 last:border-b-0',
        compact && 'py-4'
      )}
    >
      <Link
        href={to(`/persona-edit/${p.id}`)}
        aria-disabled={busy || undefined}
        tabIndex={busy ? -1 : undefined}
        onClick={busy ? (event) => event.preventDefault() : undefined}
        className={cn(
          'flex min-w-0 flex-1 items-start gap-4 rounded-control text-t1 no-underline transition-opacity hover:opacity-80 focus-visible:bg-foreground/[0.04]',
          busy && 'pointer-events-none opacity-60'
        )}
      >
        {content}
      </Link>
      <DropdownMenu
        align="end"
        trigger={
          <span className="grid size-10 place-items-center text-t3">
            <span className="sr-only">{p.name} 옵션</span>
            <LuEllipsis aria-hidden="true" className="size-5" />
          </span>
        }
        items={actions}
      />
    </article>
  )
}
